import _ from 'lodash';
import { handleErrorInfo } from '../../utils';
import { invalidBankAccountCode } from '../../utils/error-codes';
import { prepareExportTransactionCSV } from '../../utils/transactions-utils';
import { ErrorFields, ExportType } from '../interfaces';
import { PayoutsActionType } from './actions';
import { FlowType, PayoutCreationStep, PayoutsState, Banks } from './interfaces';
import { ExchangeRates } from '../interfaces';

const initialCreatePayoutState: PayoutsState['createPayout'] = {
  step: PayoutCreationStep.NONE,
  accountSubView: 'country',
  createPayoutData: {},
  bankOptions: [],
  contacts: [],
  contactsLoading: false,
  contactsError: false,
  requiredFields: {
    loading: false,
    error: false,
    response: undefined,
    fields: [],
  },
  fee: {
    loading: false,
    error: false,
    data: undefined,
  },
  partialValidation: {
    loading: false,
    error: false,
    success: false,
    errorInfo: undefined,
    errorFields: undefined,
  },
  confirm: {
    loading: false,
    error: false,
    success: false,
    errorInfo: undefined,
  },
  verifyOtpConfigured: {
    loading: false,
    error: false,
    success: false,
    errorInfo: undefined,
    otpConfigured: false,
  },
  enabledCountryConfigurations: {
    loading: false,
    error: false,
    data: [],
  },
  currencies: {
    loading: false,
    error: false,
    exchangeRates: {},
  },
};

const initialState: PayoutsState = {
  loading: true,
  error: false,
  allPayouts: undefined,
  payoutDetail: {
    loading: false,
    error: false,
    errorInfo: undefined,
    data: undefined,
  },
  requestPayout: {
    loading: false,
    error: false,
    errorInfo: undefined,
    errorFields: undefined,
    banks: {},
    success: false,
  },
  export: {
    loading: false,
    error: false,
    errorInfo: undefined,
    show: false,
    data: undefined,
  },
  contactsExport: {
    loading: false,
    error: false,
    errorInfo: undefined,
    show: false,
    data: undefined,
    exportType: undefined,
  },
  contactsList: {
    loading: true,
    error: false,
    data: [],
  },
  createPayout: initialCreatePayoutState,
};

const PayoutsReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case PayoutsActionType.GET_PAYOUTS: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case PayoutsActionType.GET_PAYOUTS_SUCCESS: {
      return {
        ...state,
        loading: false,
        error: false,
        allPayouts: {
          ...action.response,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUTS_ERROR: {
      return {
        ...state,
        loading: false,
        error: true,
        allPayouts: null,
      };
    }
    case PayoutsActionType.GET_BANKS: {
      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: true,
          error: false,
          errorInfo: undefined,
          banks: {},
          success: false,
        },
      };
    }
    case PayoutsActionType.GET_BANKS_SUCCESS: {
      const banks: Banks = {};
      const bankOptions: Array<{ code: string; name: string }> = [];
      action.response.forEach((b: { code: string | number; name: string }) => {
        const code = String(b.code);
        banks[`BANK_CODE_${code}`] = b.name;
        bankOptions.push({ code, name: b.name });
      });
      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: false,
          error: false,
          errorInfo: undefined,
          banks,
        },
        createPayout: {
          ...state.createPayout,
          bankOptions,
        },
      };
    }
    case PayoutsActionType.GET_BANKS_ERROR: {
      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: false,
          error: true,
          errorInfo: handleErrorInfo(PayoutsActionType.GET_BANKS_ERROR, action.error),
          banks: {},
        },
      };
    }
    case PayoutsActionType.REQUEST_PAYOUT: {
      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: true,
          error: false,
          errorInfo: undefined,
          errorFields: undefined,
        },
      };
    }
    case PayoutsActionType.REQUEST_PAYOUT_SUCCESS: {
      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: false,
          error: false,
          errorInfo: undefined,
          success: true,
        },
      };
    }
    case PayoutsActionType.REQUEST_PAYOUT_ERROR: {
      const errorInfo = handleErrorInfo(PayoutsActionType.REQUEST_PAYOUT_ERROR, action.error);
      const errorFields: ErrorFields = {};

      if (errorInfo && errorInfo.code === invalidBankAccountCode) {
        errorFields.bankAccount = action.error.sentValues.bankAccount.bankAccount;
      }
      if (!_.isEmpty(errorFields)) {
        return {
          ...state,
          requestPayout: {
            ...state.requestPayout,
            loading: false,
            error: false,
            errorFields,
          },
        };
      }

      return {
        ...state,
        requestPayout: {
          ...state.requestPayout,
          loading: false,
          error: true,
          errorInfo,
        },
      };
    }
    case PayoutsActionType.SET_PAYOUT_DETAIL: {
      return {
        ...state,
        payoutDetail: {
          ...state.payoutDetail,
          loading: false,
          error: false,
          errorInfo: undefined,
          data: action.data,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_DETAIL: {
      return {
        ...state,
        payoutDetail: {
          ...state.payoutDetail,
          loading: true,
          error: false,
          errorInfo: undefined,
          data: undefined,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_DETAIL_SUCCESS: {
      return {
        ...state,
        payoutDetail: {
          ...state.payoutDetail,
          loading: false,
          data: action.response,
        },
      };
    }
    case PayoutsActionType.SHOW_EXPORT_PAYOUTS: {
      return {
        ...state,
        export: {
          ...state.export,
          show: action.show,
          ...(action.show
            ? {
                loading: false,
                error: false,
                errorInfo: undefined,
                data: undefined,
              }
            : {}),
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUTS: {
      return {
        ...state,
        exportType: action.exportType,
        export: {
          ...state.export,
          loading: true,
          error: false,
          errorInfo: undefined,
          data: undefined,
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUTS_SUCCESS: {
      const exportData =
        action.exportType === ExportType.PDF ? action.response : prepareExportTransactionCSV(action.response.text);
      return {
        ...state,
        export: {
          ...state.export,
          loading: false,
          data: exportData,
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUTS_ERROR: {
      return {
        ...state,
        export: {
          ...state.export,
          loading: false,
          error: true,
          errorInfo: handleErrorInfo(PayoutsActionType.EXPORT_PAYOUTS_ERROR, action.error),
        },
      };
    }
    case PayoutsActionType.SHOW_EXPORT_PAYOUT_CONTACTS: {
      return {
        ...state,
        contactsExport: {
          ...state.contactsExport,
          show: action.show,
          ...(action.show
            ? {
                loading: false,
                error: false,
                errorInfo: undefined,
                data: undefined,
              }
            : {}),
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUT_CONTACTS: {
      return {
        ...state,
        contactsExport: {
          ...state.contactsExport,
          loading: true,
          error: false,
          errorInfo: undefined,
          data: undefined,
          exportType: action.exportType,
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUT_CONTACTS_SUCCESS: {
      const payload = action.response?.text;
      const isEmpty =
        payload === undefined ||
        payload === null ||
        payload === '' ||
        (payload instanceof ArrayBuffer && payload.byteLength === 0);

      return {
        ...state,
        contactsExport: {
          ...state.contactsExport,
          loading: false,
          data: isEmpty ? '' : payload,
        },
      };
    }
    case PayoutsActionType.EXPORT_PAYOUT_CONTACTS_ERROR: {
      return {
        ...state,
        contactsExport: {
          ...state.contactsExport,
          loading: false,
          error: true,
          errorInfo: handleErrorInfo(PayoutsActionType.EXPORT_PAYOUT_CONTACTS_ERROR, action.error),
        },
      };
    }
    case PayoutsActionType.START_CREATE_PAYOUT: {
      return {
        ...state,
        createPayout: {
          ...initialCreatePayoutState,
          step: PayoutCreationStep.ACCOUNT_DATA,
        },
      };
    }
    case PayoutsActionType.SET_CREATE_PAYOUT_STEP: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          step: action.step,
        },
      };
    }
    case PayoutsActionType.SET_CREATE_PAYOUT_SUB_VIEW: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          accountSubView: action.subView,
        },
      };
    }
    case PayoutsActionType.SET_CREATE_PAYOUT_DATA: {
      const { bankOptions, ...createPayoutData } = action.data;
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          bankOptions: bankOptions ?? state.createPayout.bankOptions,
          createPayoutData: {
            ...state.createPayout.createPayoutData,
            ...createPayoutData,
          },
        },
      };
    }
    case PayoutsActionType.GET_REQUIRED_FIELDS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          requiredFields: {
            loading: true,
            error: false,
            response: undefined,
            fields: [],
          },
        },
      };
    }
    case PayoutsActionType.GET_REQUIRED_FIELDS_SUCCESS: {
      const fields = [
        ...action.response.baseRequiredFields,
        ...action.response.additionalRequiredFields,
        ...action.response.allConfiguredFields,
      ].sort((fieldA, fieldB) => fieldA.displayOrder - fieldB.displayOrder);
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          createPayoutData: {
            ...state.createPayout.createPayoutData,
            flowType: action.response.flowType as FlowType,
            transferCountry: action.response.country,
          },
          requiredFields: {
            loading: false,
            error: false,
            response: action.response,
            fields,
          },
        },
      };
    }
    case PayoutsActionType.GET_REQUIRED_FIELDS_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          requiredFields: {
            loading: false,
            error: true,
            response: undefined,
            fields: [],
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_FEE: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          fee: {
            loading: true,
            error: false,
            data: state.createPayout.fee.data,
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_FEE_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          fee: {
            loading: false,
            error: false,
            data: action.response,
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_FEE_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          fee: {
            loading: false,
            error: true,
            data: undefined,
          },
        },
      };
    }
    case PayoutsActionType.VALIDATE_PARTIAL_PAYOUT: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          partialValidation: {
            loading: true,
            error: false,
            success: false,
            errorInfo: undefined,
            errorFields: undefined,
          },
        },
      };
    }
    case PayoutsActionType.VALIDATE_PARTIAL_PAYOUT_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          partialValidation: {
            loading: false,
            error: false,
            success: true,
            errorInfo: undefined,
            errorFields: undefined,
          },
        },
      };
    }
    case PayoutsActionType.VALIDATE_PARTIAL_PAYOUT_ERROR: {
      const errorInfo = handleErrorInfo(PayoutsActionType.VALIDATE_PARTIAL_PAYOUT_ERROR, action.error);
      const errorFields: ErrorFields = {};
      const sentValues = action.error?.sentValues || {};
      const responseErrorFields = errorInfo?.errorFields || [];

      responseErrorFields.forEach((field: string) => {
        if (sentValues[field] !== undefined && sentValues[field] !== null) {
          errorFields[field] = String(sentValues[field]);
        }
      });

      if (!_.isEmpty(errorFields)) {
        return {
          ...state,
          createPayout: {
            ...state.createPayout,
            partialValidation: {
              loading: false,
              error: false,
              success: false,
              errorInfo: undefined,
              errorFields,
            },
          },
        };
      }

      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          partialValidation: {
            loading: false,
            error: true,
            success: false,
            errorInfo,
            errorFields: undefined,
          },
        },
      };
    }
    case PayoutsActionType.CONFIRM_PAYOUT: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          confirm: {
            loading: true,
            error: false,
            success: false,
            errorInfo: undefined,
          },
        },
      };
    }
    case PayoutsActionType.CONFIRM_PAYOUT_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          confirm: {
            loading: false,
            error: false,
            success: true,
            errorInfo: undefined,
          },
        },
      };
    }
    case PayoutsActionType.CONFIRM_PAYOUT_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          confirm: {
            loading: false,
            error: true,
            success: false,
            errorInfo: handleErrorInfo(PayoutsActionType.CONFIRM_PAYOUT_ERROR, action.error),
          },
        },
      };
    }
    case PayoutsActionType.RESET_CREATE_PAYOUT: {
      return {
        ...state,
        createPayout: initialCreatePayoutState,
      };
    }
    case PayoutsActionType.VERIFY_OTP_CONFIGURED: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          verifyOtpConfigured: {
            loading: true,
            error: false,
            otpConfigured: false,
            success: false,
            errorInfo: undefined,
          },
        },
      };
    }
    case PayoutsActionType.VERIFY_OTP_CONFIGURED_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          verifyOtpConfigured: {
            loading: false,
            error: false,
            otpConfigured: action.response.otpConfigured,
            success: true,
            errorInfo: undefined,
          },
        },
      };
    }
    case PayoutsActionType.VERIFY_OTP_CONFIGURED_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          verifyOtpConfigured: {
            loading: false,
            error: true,
            otpConfigured: false,
            success: false,
            errorInfo: handleErrorInfo(PayoutsActionType.VERIFY_OTP_CONFIGURED_ERROR, action.error),
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_COUNTRY_CONFIGURATIONS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          enabledCountryConfigurations: {
            loading: true,
            error: false,
            data: [],
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_COUNTRY_CONFIGURATIONS_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          enabledCountryConfigurations: {
            loading: false,
            error: false,
            data: action.response,
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_COUNTRY_CONFIGURATIONS_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          enabledCountryConfigurations: {
            loading: false,
            error: true,
            data: [],
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CURRENCIES: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          currencies: {
            loading: true,
            error: false,
            exchangeRates: {},
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CURRENCIES_SUCCESS: {
      const exchangeRates: ExchangeRates = {};
      let localCurrency;
      let exchangeRateMinusSpread = 0;
      let localExchangeRate = 0;

      action.response.forEach((currencyItem: { currency: string; exchangeRate: number; exchangeRateMinusSpread: number }) => {
        if (currencyItem.currency !== 'USD') {
          localCurrency = currencyItem.currency;
          localExchangeRate = currencyItem.exchangeRate;
          exchangeRateMinusSpread = currencyItem.exchangeRateMinusSpread;
        }
        exchangeRates[currencyItem.currency] = currencyItem.exchangeRate;
      });
      exchangeRates.base = (localExchangeRate + exchangeRateMinusSpread) / 2;

      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          currencies: {
            loading: false,
            error: false,
            exchangeRates,
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CURRENCIES_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          currencies: {
            loading: false,
            error: true,
            exchangeRates: {},
          },
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          contactsLoading: true,
          contactsError: false,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS_SUCCESS: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          contacts: action.response,
          contactsLoading: false,
          contactsError: false,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS_ERROR: {
      return {
        ...state,
        createPayout: {
          ...state.createPayout,
          contacts: [],
          contactsLoading: false,
          contactsError: true,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS_LIST: {
      return {
        ...state,
        contactsList: {
          ...state.contactsList,
          loading: true,
          error: false,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS_LIST_SUCCESS: {
      return {
        ...state,
        contactsList: {
          loading: false,
          error: false,
          data: action.response,
        },
      };
    }
    case PayoutsActionType.GET_PAYOUT_CONTACTS_LIST_ERROR: {
      return {
        ...state,
        contactsList: {
          loading: false,
          error: true,
          data: [],
        },
      };
    }
    default:
      return state;
  }
};

export default PayoutsReducer;
